/**
 * The shoot's calendar invites (Aziz, 2026-09-27): who got them, a Retry when Google
 * hiccupped or a doctor's email was just added, an Add to calendar link, and a
 * shortcut to book a meeting about this shoot.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarCheck, CalendarPlus, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { api } from '@/lib/api';
import type { ShootSession } from '@/lib/queries';
import NewMeetingDialog from './NewMeetingDialog';

interface Invites { team?: number; clinic?: number; clinic_fallback?: boolean; error?: string | null; synced_at?: string; cancelled?: boolean; dry_run?: boolean }

/** A Google Calendar "add event" link, for anyone who wants it in another calendar. */
function gcalLink(s: ShootSession): string {
  const f = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const start = new Date(s.shoot_at);
  const end = new Date(start.getTime() + (s.duration_minutes || 240) * 60000);
  const p = new URLSearchParams({ action: 'TEMPLATE', text: `KAF shoot: ${s.title}`, dates: `${f(start)}/${f(end)}`,
    details: `${window.location.origin}/shoots/${s.id}`, location: s.location || '' });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

export default function ShootInvitesCard({ s, canPlan }: { s: ShootSession & { invites?: Invites }; canPlan: boolean }) {
  const qc = useQueryClient();
  const [meeting, setMeeting] = useState(false);
  const inv: Invites = s.invites ?? {};
  const retry = useMutation({
    mutationFn: async () => (await api.post(`/shoots/${s.id}/invites/sync`)).data,
    onSuccess: () => { toast.success('Invites updated'); qc.invalidateQueries({ queryKey: ['shoots'] }); },
    onError: () => toast.error('Could not reach the calendar. Try again in a minute.'),
  });
  const scheduled = s.status === 'scheduled' || s.status === 'planned';

  return (
    <Card className="mb-4" data-testid="shoot-invites">
      <div className="flex flex-wrap items-start gap-3">
        <CalendarCheck size={18} className="text-accent-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-medium text-muted-strong">Calendar invites</div>
          {!scheduled ? (
            <p className="text-xs text-muted mt-0.5">{s.status === 'proposed' ? 'They go out once the shoot is approved, so nobody blocks a day that may not happen.' : 'No invites: the shoot is not scheduled.'}</p>
          ) : inv.synced_at ? (
            <ul className="text-xs text-muted mt-1 space-y-0.5">
              <li>Team: {inv.team ?? 0} {inv.team === 1 ? 'person' : 'people'} (crew, content creator, art director, planner and approver), with the crew notes</li>
              <li>Clinic: {inv.clinic ? `${inv.clinic} ${inv.clinic === 1 ? 'person' : 'people'}` : 'nobody yet'}
                {inv.clinic_fallback && ', sent to the clinic contact because no doctor has an email on file'}
                {!inv.clinic && ': add the doctor\'s email or a clinic contact, then Retry'}</li>
              {inv.dry_run && <li className="text-amber-300">Dry run: Google is not connected here, so nothing was sent.</li>}
              {inv.error && <li className="text-red-300">Last attempt failed: {inv.error}</li>}
            </ul>
          ) : <p className="text-xs text-muted mt-0.5">Not sent yet.</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {scheduled && <a href={gcalLink(s)} target="_blank" rel="noreferrer" className="inline-flex items-center h-8 px-3 rounded-lg text-xs bg-bg-card border border-slate-700 text-muted-strong hover:border-accent-500">Add to my calendar</a>}
          {scheduled && canPlan && <Button size="sm" variant="secondary" icon={<RefreshCw size={12} />} loading={retry.isPending} onClick={() => retry.mutate()} data-testid="invites-retry">Retry</Button>}
          <Button size="sm" variant="secondary" icon={<CalendarPlus size={12} />} onClick={() => setMeeting(true)} data-testid="shoot-book-meeting">Book a meeting</Button>
        </div>
      </div>
      {meeting && (
        <NewMeetingDialog open onClose={() => setMeeting(false)} prefill={{
          kind: 'internal', title: `About the shoot: ${s.title}`.slice(0, 200), clientIds: [s.client_id],
          attendeeIds: s.crew_user_ids, shootId: s.id,
          items: s.creatives.map((c) => ({ kind: 'creative' as const, id: c.id, label: `${c.code} ${c.title}` })),
        }} />
      )}
    </Card>
  );
}
